import { lazy } from 'react';
import type { RouteObject } from 'react-router-dom';

import { SCREENS } from '@/app/navigation/screens';
import { ROUTES } from '@/config/routes';

const WorkInProgressPage = lazy(() =>
  import('./WorkInProgressPage').then((m) => ({ default: m.WorkInProgressPage })),
);

/**
 * A route for every registered screen that has no page yet.
 *
 * <h2>Derived from the screen registry, not from the menu</h2>
 *
 * It read `NAV_ITEMS` until 8 October 2026, which made the sidebar the route table: taking a
 * row out of the rail **deleted its route**. `SCREENS` is the registry now, so a screen with
 * no menu row — Lead Meeting Intelligence, My Account, the two agents — is still routable.
 * `app/navigation/screens.ts` has the reasoning.
 *
 * <p>The built screens are excluded. As each of the others gets a real page, its route moves
 * into that feature's own module and its id joins the list below: this array is whatever is
 * left.
 */
const BUILT_ROUTES: readonly string[] = [
  ROUTES.dashboard,
  ROUTES.account,
  ROUTES.team,
  ROUTES.warmupReceivers,
  ROUTES.platformBilling,
  ROUTES.platformBillingPlans,
  ROUTES.platformBillingRequests,
  ROUTES.emailWarmup,
  ROUTES.emailWarmupAccount,
  ROUTES.emailWarmupCreate,
  ROUTES.emailWarmupBilling,
  ROUTES.brandAnalyser,
  ROUTES.aiAgent,
  ROUTES.seoAgent,
  ROUTES.seoAgentWelcome,
  ROUTES.seoAgentSetup,
  ROUTES.seoAgentTab,
  ROUTES.seoAgentTask,
  ROUTES.seoAgentTaskChanges,
  ROUTES.seoAgentSettings,
  ROUTES.seoAgentCustomContent,
  ROUTES.linkedinAgent,
  ROUTES.linkedinSettings,
  ROUTES.linkedinConnect,
  ROUTES.linkedinWelcome,
  ROUTES.linkedinCompanyDetails,
  ROUTES.linkedinConfiguration,
  ROUTES.linkedinLaunch,
  ROUTES.linkedinReferences,
  ROUTES.linkedinProspects,
  ROUTES.linkedinProspectDetail,
  ROUTES.linkedinStrategy,
  ROUTES.linkedinConversations,
  ROUTES.linkedinContent,
  ROUTES.linkedinApprovals,
  ROUTES.linkedinActivity,
  ROUTES.linkedinAnalytics,
  ROUTES.linkedinReview,
  ROUTES.reviews,
  ROUTES.reviewsResponses,
  ROUTES.reviewsAggregation,
  ROUTES.reviewForms,
  ROUTES.reviewQuestions,
  ROUTES.supportAdmin,
  ROUTES.developerTools,
  ROUTES.developerPricing,
  ROUTES.apiConsole,
  ROUTES.apiKeyDetail,
  ROUTES.developerApiDocumentation,
  ROUTES.developerMcpDocumentation,
  ROUTES.brandApiDocs,
  ROUTES.developerMcp,
  ROUTES.mcpProtocolDetail,
  ROUTES.developerIntegrations,
];

/**
 * The product a placeholder should ask about, when asking makes sense.
 *
 * <p>Exactly one, or none. A screen backed by two products — `/agents` fronts
 * `seo_ai_agent` and `linkedin_ai_agent` — opens when **either** is owned, and no single
 * endpoint can answer that: probing one would offer an upgrade to somebody who already owns
 * the other. So that screen asks about neither.
 *
 * <p>Its two children each name one agent, which is why they can ask and it cannot.
 */
function soleProductOf(screen: (typeof SCREENS)[number]): string | undefined {
  return screen.productKeys?.length === 1 ? screen.productKeys[0] : undefined;
}

export const placeholderRoutes: RouteObject[] = [
  ...SCREENS.filter((screen) => !BUILT_ROUTES.includes(screen.route)).map((screen) => {
    const productKey = soleProductOf(screen);

    return {
      path: screen.route,
      element: (
        <WorkInProgressPage
          title={screen.title}
          description={screen.description}
          // Spread rather than passed as undefined: `exactOptionalPropertyTypes`
          // refuses an explicit undefined for an optional prop, and it is right
          // to -- "absent" and "present but unknown" are different states and
          // this one is genuinely absent.
          {...(productKey === undefined ? {} : { productKey })}
        />
      ),
    };
  }),
];
